import { RadioGroup } from '@headlessui/react';
import TwoColFullScreen from '@layouts/v2/twocolfullscreen.layout';
import ButtonV2, {
  ButtonV2Rounded,
  ButtonV2Size,
  ButtonV2Style,
} from '@ui-kit/v2/Button';
import { CheckBoxWithLabel } from '@ui-kit/v2/CheckboxV2';
import { RadioV2, buildPropsRadioGroupFormik } from '@ui-kit/v2/RadioV2';
import classNames from 'classnames';
import { Field, FormikProps, FormikProvider, useFormik } from 'formik';
import { includes, isEqual } from 'lodash';
import Image from 'next/image';
import { Fragment } from 'react';
import { HiOutlineXCircle } from 'react-icons/hi';
import { PdpaValue, validSchema } from 'src/account/pdpaForm.schema';
import useTranslation from 'src/i18n/useTranslation';

const PdpaSignup = (props) => {
  const { submitSignupForm, error } = props;
  const { t } = useTranslation();

  const formik = useFormik<PdpaValue>({
    initialValues: {
      receiveNewsPermission: null,
      dataCollectionPermission: null,
      contactFeedbackPermission: null,
    },
    validateOnBlur: false,
    validationSchema: validSchema,
    onSubmit: submitSignupForm,
  });

  const handleChangeAllChoices = (
    formik: FormikProps<PdpaValue>,
    checked: boolean,
  ) => {
    const value = checked || includes(formik.values as any, false);

    formik.setValues({
      ...formik.values,
      receiveNewsPermission: value,
      dataCollectionPermission: value,
      contactFeedbackPermission: value,
    });
  };

  return (
    <TwoColFullScreen
      leftContent={
        <div>
          <h3 className="mb-4 text-h3 font-bold">
            {t('privacyPolicyPage.title')}
          </h3>
          <p className="mb-6 text-heading font-semibold">
            {t('privacyPolicyPage.subtitle')}
          </p>
          <ButtonV2
            variantClassName={classNames(
              ButtonV2Rounded.large,
              ButtonV2Style.primary,
              ButtonV2Size.small,
              'w-1/3 h-fit',
            )}
            buttonProps={{
              disabled:
                isEqual(formik.values, formik.initialValues) ||
                !formik.isValid ||
                formik.isValidating ||
                formik.isSubmitting,
              onClick: () => formik.handleSubmit(),
            }}
          >
            {t('save')}
          </ButtonV2>
          {error && (
            <p className="mt-2 text-caption font-regular text-redv2-400">
              {error}
            </p>
          )}
        </div>
      }
      mobileHeader={
        <div className=" w-full bg-white">
          <div className="relative flex h-[108px] w-full">
            <Image
              src="/assets/signup-mobile-header.svg"
              layout="fill"
              objectFit="cover"
              height={108}
            />
          </div>
          <div className="p-6 text-subheadingv2 font-semibold">
            {t('pdpaPage.header')}
          </div>
        </div>
      }
    >
      <div className="h-full min-h-0 overflow-auto lg:flex lg:items-center lg:pl-15 lg:pr-20">
        <FormikProvider value={formik}>
          <form
            className="flex w-full flex-col gap-y-6 px-6 pb-26 text-p1 lg:gap-y-12 lg:p-0"
            onSubmit={() => formik.handleSubmit()}
          >
            <div className="hidden text-p2 lg:block lg:self-end">
              <CheckBoxWithLabel
                labelProps={{
                  title: t('pdpaPage.acceptAll'),
                }}
                checkboxV2Props={{
                  isChecked:
                    includes(formik.values as any, true) &&
                    formik.isValid &&
                    !formik.isValidating,
                  onChange: (checked) =>
                    handleChangeAllChoices(formik, checked),
                  indeterminate: includes(formik.values as any, false),
                }}
              />
            </div>
            {Object.keys(formik.values)
              .filter((key) => key.includes('Permission'))
              .map((key, idx) => (
                <Fragment key={key}>
                  <p>{t(`pdpaPage.q_${idx + 1}`)}</p>
                  <Field as="select" name={key} className="grid grid-cols-2">
                    {(props) => (
                      <RadioGroup
                        {...buildPropsRadioGroupFormik(props)}
                        as="div"
                        className="grid grid-cols-2 text-p2"
                      >
                        <RadioGroup.Option value={true} className="flex flex-1">
                          {(radioOpts) => (
                            <div className="inline-flex items-center gap-x-2">
                              <RadioV2 {...radioOpts} />
                              <RadioGroup.Description
                                as="div"
                                className={'text-p2 font-regular'}
                              >
                                {t('pdpaPage.accept')}
                              </RadioGroup.Description>
                            </div>
                          )}
                        </RadioGroup.Option>
                        <RadioGroup.Option
                          value={false}
                          className="flex flex-1"
                        >
                          {(radioOpts) => (
                            <div className=" inline-flex items-center gap-x-2">
                              <RadioV2 {...radioOpts} />
                              <RadioGroup.Description
                                as="div"
                                className={'text-p2 font-regular'}
                              >
                                {t('pdpaPage.decline')}
                              </RadioGroup.Description>
                            </div>
                          )}
                        </RadioGroup.Option>
                      </RadioGroup>
                    )}
                  </Field>
                  <hr className="border-grayv2-200 lg:hidden" />
                </Fragment>
              ))}

            <span className="whitespace-pre-wrap">
              {t('pdpaPage.footdescription')}
            </span>

            <div className="lg:hidden">
              {error && (
                <div className="mb-6 flex rounded-md bg-redv2-50 px-2 py-4 text-left text-p1 font-semibold text-redv2-700">
                  <span className=" mr-2 text-xl">
                    <HiOutlineXCircle />
                  </span>
                  {error}
                </div>
              )}
              <CheckBoxWithLabel
                labelProps={{
                  title: t('pdpaPage.acceptAll'),
                }}
                checkboxV2Props={{
                  isChecked:
                    includes(formik.values as any, true) &&
                    formik.isValid &&
                    !formik.isValidating,
                  onChange: (checked) =>
                    handleChangeAllChoices(formik, checked),
                  indeterminate: includes(formik.values as any, false),
                }}
              />
              <ButtonV2
                variantClassName={classNames(
                  ButtonV2Size.medium,
                  ButtonV2Style.primary,
                  ButtonV2Rounded.large,
                  'lg:hidden w-full h-fit mt-2.5',
                )}
                buttonProps={{
                  disabled:
                    isEqual(formik.values, formik.initialValues) ||
                    !formik.isValid ||
                    formik.isValidating ||
                    formik.isSubmitting,
                  onClick: () => formik.handleSubmit(),
                }}
              >
                {t('save')}
              </ButtonV2>
            </div>
          </form>
        </FormikProvider>
      </div>
    </TwoColFullScreen>
  );
};

export default PdpaSignup;
